<script>
export default {
  name: 'ReportSave',
  props: {
    reportName: { required: true, type: String },
    reportParameters: { type: Object },
    reportRecords: { required: true, type: Array },
    reportFields: { type: Array },
    chartType: { type: String },
    savedData: { type: Object }
  },
  data() {
    return {
      saveNotes: null,
      savePermissionsPublic: false,
      savePermissionsGroups: [],
      savePermissionsPeople: [],
      searchGroups: [],
      searchPeople: [],
      saveMutation: /* GraphQL */ `
        mutation ReportSaveFileMutation(
          $reportName: String!
          $saveData: JSON
          $savePermissions: JSON
          $saveNotes: String
        ) {
          saveData: createMetricsDashSave(
            input: {
              metricsDashSave: {
                reportName: $reportName
                saveData: $saveData
                savePermissions: $savePermissions
                saveNotes: $saveNotes
              }
            }
          ) {
            metricsDashSave {
              saveId
              saveNotes
              savePermissions
            }
          }
        }
      `,
      updateMutation: /* GraphQL */ `
        mutation ReportSaveFileMutation(
          $saveId: Int!
          $reportName: String!
          $saveData: JSON
          $savePermissions: JSON
          $saveNotes: String
        ) {
          saveData: updateMetricsDashSaveBySaveId(
            input: {
              metricsDashSavePatch: {
                reportName: $reportName
                saveData: $saveData
                savePermissions: $savePermissions
                saveNotes: $saveNotes
              }
              saveId: $saveId
            }
          ) {
            metricsDashSave {
              saveId
              saveNotes
              savePermissions
            }
          }
        }
      `
    }
  },
  computed: {
    publicLink() {
      return `${window.location.origin}/metrics/public/${this.savedData.saveId}`
    }
  },
  watch: {
    savedData: {
      deep: true,
      handler: function() {
        this.loadSavedData()
      }
    }
  },
  methods: {
    saveResults() {
      let savePermissions = {}
      if (this.savePermissionsPeople.map(entry => entry.personId).length) {
        savePermissions.users = this.savePermissionsPeople.map(
          entry => entry.personId
        )
      }
      if (this.savePermissionsGroups.map(entry => entry.groupId).length) {
        savePermissions.groups = this.savePermissionsGroups.map(
          entry => entry.groupId
        )
      }
      if (this.savePermissionsPublic) {
        savePermissions.public = true
      }
      if (JSON.stringify(savePermissions) == '{}') {
        savePermissions = null
      }
      this.graphql(this.savedData ? this.updateMutation : this.saveMutation, {
        saveId: this.savedData ? this.savedData.saveId : null,
        reportName: this.reportName,
        saveNotes: this.saveNotes,
        savePermissions: savePermissions,
        saveData: {
          reportRecords: this.reportRecords,
          reportFields: this.reportFields,
          reportParameters: this.reportParameters,
          chartType: this.chartType
        }
      }).then(data => {
        window.reportSaved = true
        window.reportSaveId = data.saveData.metricsDashSave.saveId
        this.$root.$emit('refresh-nav') // make sure the saved reports shows up in the menu
        this.$emit('report-saved', {
          saveId: data.saveData.metricsDashSave.saveId,
          saveNotes: data.saveData.metricsDashSave.saveNotes,
          savePermissions: data.saveData.metricsDashSave.savePermissions
        })
      })
    },
    loadSearchPeopleGroups() {
      this.graphqlAllPages(
        this.buildGraphqlQuery(
          'allPeople',
          [
            {
              nodes: true,
              fields: ['personId', 'fullName', 'email']
            }
          ],
          {
            searchName: 'LookupOptions_PeopleSearch',
            conditions: {
              statusCode: { default: 'F' }
            },
            params: [
              {
                field: 'orderBy',
                passArray: true,
                type: 'PeopleOrderBy',
                default: 'FULL_NAME_ASC'
              }
            ]
          }
        )[0],
        { numRecords: 1000, recordOffset: 0 },
        60 * 5 // 5 minute cache time
      ).then(data => {
        this.searchPeople = data.allPeople.nodes
      })

      this.graphqlAllPages(
        this.buildGraphqlQuery(
          'allGroups',
          [
            {
              nodes: true,
              fields: ['groupId', 'groupName']
            }
          ],
          {
            searchName: 'LookupOptions_GroupSearch',
            params: [
              {
                field: 'orderBy',
                passArray: true,
                type: 'GroupsOrderBy',
                default: 'GROUP_NAME_ASC'
              }
            ]
          }
        )[0],
        { numRecords: 1000, recordOffset: 0 },
        60 * 5 // 5 minute cache time
      ).then(data => {
        this.searchGroups = data.allGroups.nodes
      })
    },
    loadSavedData() {
      if (!this.searchPeople.length || !this.searchGroups.length) {
        setTimeout(this.loadSavedData, 100)
        return
      }
      if (this.savedData) {
        this.saveNotes = this.savedData.saveNotes
        if (this.savedData.savePermissions) {
          this.savePermissionsGroups = (
            this.savedData.savePermissions.groups || []
          ).map(groupId =>
            this.searchGroups.find(group => group.groupId == groupId)
          )
          this.savePermissionsPeople = (
            this.savedData.savePermissions.users || []
          ).map(personId =>
            this.searchPeople.find(person => person.personId == personId)
          )
          this.savePermissionsPublic =
            this.savedData.savePermissions.public || false
        }
      }
    },
    publicLinkToClipboard() {
      navigator.clipboard.writeText(this.publicLink)
    }
  },
  mounted() {
    window.reportSaved = false
    this.loadSavedData()
  }
}
</script>

<template>
  <b-modal
    id="save-results-modal"
    title="Save / Share Results"
    @ok="saveResults"
    @show="loadSearchPeopleGroups"
    size="lg"
    :no-close-on-esc="true"
  >
    <b-form-group
      label-for="saveNotes"
      class="form-field-group"
      label-class="font-weight-bold pb-0"
      label="Notes"
    >
      <b-input-group>
        <b-form-textarea
          v-model="saveNotes"
          name="saveNotes"
          autocomplete="off"
        />
      </b-input-group>
    </b-form-group>
    <b-form-group
      label-for="savePermissions"
      class="form-field-group"
      label-class="font-weight-bold pb-0"
      label="Permissions"
    >
      <b-form-group
        label-for="savePermissionsPublic"
        label-class="font-weight-bold pb-0"
        label="Anyone with link"
        label-cols="2"
      >
        <b-input-group>
          <b-form-checkbox
            v-model="savePermissionsPublic"
            name="savePermissionsPublic"
          />
          <template v-if="savePermissionsPublic && savedData">
            {{ publicLink }}
            <b-button
              id="copyClipboardButtion"
              class="button ml-3"
              @click="publicLinkToClipboard"
            >
              Copy to Clipboard
            </b-button>
            <b-popover target="copyClipboardButtion" triggers="click blur">
              Link Copied
            </b-popover>
          </template>
        </b-input-group>
      </b-form-group>
      <multiselect
        v-model="savePermissionsGroups"
        :options="searchGroups"
        :multiple="true"
        :close-on-select="false"
        :clear-on-select="false"
        :preserve-search="true"
        placeholder="Select Group(s)"
        label="groupName"
        track-by="groupName"
      >
        <template slot="selection" slot-scope="{ values, search, isOpen }">
          <span class="multiselect__single" v-if="values.length && !isOpen">
            {{ values.map(entry => entry.groupName).join(', ') }}
          </span>
        </template>
      </multiselect>
      <multiselect
        v-model="savePermissionsPeople"
        :options="searchPeople"
        :multiple="true"
        :close-on-select="false"
        :clear-on-select="false"
        :preserve-search="true"
        placeholder="Select People"
        label="fullName"
        track-by="fullName"
      >
        <template slot="selection" slot-scope="{ values, search, isOpen }">
          <span class="multiselect__single" v-if="values.length && !isOpen">
            {{ values.map(entry => entry.fullName).join(', ') }}
          </span>
        </template>
      </multiselect>
    </b-form-group>
    <br />
  </b-modal>
</template>
